import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { tap, map, catchError, takeUntil, switchMap } from 'rxjs/operators';

import {
  IGenerateImage,
  IGenerateImageRequest,
  IGenerateResponse,
  IGenerationProgress,
  IUnifiedMediaStatusResponse,
  IUnifiedMediaProvisionsResponse,
  IUnifiedMediaResponse
} from '../interfaces/media.interfaces';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MediaService {
  private readonly apiUrl: string = `${environment.apiBaseUrl}`;
  private pendingGenerationsMap: Map<string, IGenerateImage> = new Map();
  private generationProgressMap: Map<string, IGenerationProgress> = new Map();
  private isInitialLoadComplete: boolean = false;
  private cancelRequestsSubject = new Subject<void>();

  // Progress calculation constants
  private readonly FAST_PHASE_DURATION: number = 10000; // 10 seconds for fast phase
  private readonly FAST_PHASE_TARGET: number = 60; // Target percentage for fast phase
  private readonly MAX_DURATION: number = 60000; // 60 seconds max wait
  private readonly MAX_PROGRESS: number = 99; // Maximum progress before completion

  public unreadCompletedGenerations$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public pendingGenerations$: BehaviorSubject<IGenerateImage[]> = new BehaviorSubject<
    IGenerateImage[]
  >([]);
  private allMediaCache: IGenerateImage[] = [];
  public allMedia$: BehaviorSubject<IGenerateImage[]> = new BehaviorSubject<IGenerateImage[]>([]);
  private isDataLoaded = false;

  constructor(private readonly http: HttpClient) {}

  initialLoad(forceReload: boolean = false): Observable<IGenerateImage[]> {
    if (!forceReload && this.isDataLoaded && this.allMediaCache.length > 0) {
      return of(this.allMediaCache);
    }
    return this.fetchAllMedia();
  }

  private fetchAllMedia(): Observable<IGenerateImage[]> {
    const endpoint = this.apiUrl + `/medias/vault`;
    return this.http.get<IGenerateImage[]>(endpoint).pipe(
      takeUntil(this.cancelRequestsSubject),
      tap(medias => {
        // Combine server media with any pending generations
        const pendingGenerations = Array.from(this.pendingGenerationsMap.values());
        const allMedia = [...pendingGenerations, ...medias];

        this.isDataLoaded = true;
        this.isInitialLoadComplete = true;

        this.updateAllMediaCache(allMedia);
      }),
      map(() => this.allMediaCache)
    );
  }

  getGenerationProgress(generationId: string): number {
    const progress = this.generationProgressMap.get(generationId);
    if (!progress) return 0;

    // If animating, always return the stored percentage (rounded)
    if (progress.isAnimating) {
      return Math.round(progress.percentage);
    }

    // If complete and not animating, return 100
    if (progress.isComplete) return 100;

    const elapsed = Date.now() - progress.startTime;

    let calculatedProgress: number;

    // Fast initial phase (0 to ~60% in 10 seconds)
    if (elapsed <= this.FAST_PHASE_DURATION) {
      const fastPhaseProgress = (elapsed / this.FAST_PHASE_DURATION) * this.FAST_PHASE_TARGET;
      calculatedProgress = Math.min(this.FAST_PHASE_TARGET, fastPhaseProgress);
    } else {
      // Slower phase (60% to 99%)
      const remainingTime = elapsed - this.FAST_PHASE_DURATION;
      const remainingDuration = this.MAX_DURATION - this.FAST_PHASE_DURATION;
      const remainingPercentage = this.MAX_PROGRESS - this.FAST_PHASE_TARGET;

      // Use logarithmic curve for the remaining progress
      const logProgress = Math.log(remainingTime + 1) / Math.log(remainingDuration + 1);
      const slowPhaseProgress = this.FAST_PHASE_TARGET + logProgress * remainingPercentage;

      calculatedProgress = Math.min(this.MAX_PROGRESS, slowPhaseProgress);
    }

    // Update the progress object with the calculated percentage (rounded)
    progress.percentage = Math.round(calculatedProgress);
    this.generationProgressMap.set(generationId, progress);

    return progress.percentage;
  }

  generateImage(
    requestBody: IGenerateImageRequest,
    newService: boolean
  ): Observable<IGenerateResponse> {
    const generationId = Date.now().toString();

    const pendingImage: IGenerateImage = {
      id: generationId,
      url: '',
      prompt: requestBody.prompt,
      creator_id: requestBody.creator_id,
      generated: false
    };

    // Initialize progress tracking
    this.generationProgressMap.set(generationId, {
      percentage: 0,
      startTime: Date.now(),
      isComplete: false
    });

    this.pendingGenerationsMap.set(generationId, pendingImage);
    this.updatePendingGenerations();

    this.updateAllMediaCache([pendingImage, ...this.allMediaCache]);

    return newService
      ? this.unifiedServiceGenerateImage(requestBody, generationId)
      : this.oldServiceGenerateImage(requestBody, generationId);
  }

  oldServiceGenerateImage(
    requestBody: IGenerateImageRequest,
    generationId: string
  ): Observable<IGenerateResponse> {
    const endpoint = this.apiUrl + `/medias/generate-image`;
    return this.http.post<IGenerateResponse>(endpoint, requestBody).pipe(
      takeUntil(this.cancelRequestsSubject),
      tap(response => {
        this.cleanupPendingGeneration(generationId);

        if (!response.items.length) {
          this.updateAllMediaCache(this.allMediaCache.filter(img => img.id !== generationId));
          this.generationProgressMap.delete(generationId);
          return;
        }

        const generatedImage = response.items[0];

        // Animate completion and then update cache
        this.animateGenerationCompletion(generationId, generatedImage);
      }),
      catchError(error => {
        this.handleErrorResponse(generationId);
        throw error;
      })
    );
  }

  unifiedServiceGenerateImage(
    requestBody: IGenerateImageRequest,
    generationId: string
  ): Observable<IGenerateResponse> {
    const endpoint = this.apiUrl + `/medias/mediaprovisions`;
    return this.http.post<IUnifiedMediaProvisionsResponse>(endpoint, requestBody).pipe(
      takeUntil(this.cancelRequestsSubject),
      switchMap(response => {
        return this.getMediaStatus(response.data.id, generationId);
      }),
      catchError(error => {
        this.handleErrorResponse(generationId);
        throw error;
      })
    );
  }

  private getMediaStatus(id: string, generationId: string): Observable<IGenerateResponse> {
    const endpoint = this.apiUrl + `/medias/mediaprovisions/${id}`;
    return this.http.get<IUnifiedMediaStatusResponse>(endpoint).pipe(
      takeUntil(this.cancelRequestsSubject),
      switchMap(response => {
        if (response.data.status === 'completed') {
          return this.getMediaItem(response.data.media_id, generationId);
        } else {
          // Poll again after 8 seconds
          return new Observable<IGenerateResponse>(observer => {
            setTimeout(() => {
              this.getMediaStatus(id, generationId).subscribe(observer);
            }, 8000);
          });
        }
      }),
      catchError(error => {
        this.handleErrorResponse(generationId);
        throw error;
      })
    );
  }

  private getMediaItem(mediaId: string, generationId: string): Observable<IGenerateResponse> {
    const endpoint = this.apiUrl + `/medias/media/${mediaId}`;
    return this.http.get<IUnifiedMediaResponse>(endpoint).pipe(
      takeUntil(this.cancelRequestsSubject),
      map(response => {
        const pendingImage = this.pendingGenerationsMap.get(generationId);
        if (!pendingImage) {
          throw new Error('Pending image not found');
        }

        const generatedImage: IGenerateImage = {
          ...pendingImage,
          url: response.data.image_url,
          generated: true
        };

        this.cleanupPendingGeneration(generationId);
        this.animateGenerationCompletion(generationId, generatedImage);

        return {
          items: [generatedImage]
        };
      }),
      catchError(error => {
        this.handleErrorResponse(generationId);
        throw error;
      })
    );
  }

  private cleanupPendingGeneration(generationId: string): void {
    this.pendingGenerationsMap.delete(generationId);
    this.updatePendingGenerations();
    const currentCount = this.unreadCompletedGenerations$.getValue();
    this.unreadCompletedGenerations$.next(currentCount + 1);
  }

  private handleErrorResponse(generationId: string): void {
    this.pendingGenerationsMap.delete(generationId);
    this.generationProgressMap.delete(generationId);
    this.updatePendingGenerations();

    const updatedMedia = this.allMediaCache.filter(img => img.id !== generationId);
    this.updateAllMediaCache(updatedMedia);
  }

  private updatePendingGenerations(): void {
    const pendingGenerations = Array.from(this.pendingGenerationsMap.values());
    this.pendingGenerations$.next(pendingGenerations);
  }

  private updateAllMediaCache(media: IGenerateImage[]): void {
    this.allMediaCache = media;
    this.allMedia$.next([...this.allMediaCache]);
  }

  private animateGenerationCompletion(generationId: string, generatedImage: IGenerateImage): void {
    const progress = this.generationProgressMap.get(generationId);
    if (!progress) return;

    // Set animating flag and ensure starting value is rounded
    progress.isAnimating = true;
    progress.percentage = Math.round(progress.percentage);
    this.generationProgressMap.set(generationId, progress);

    const start = progress.percentage;
    const end = 100;
    const duration = 1000;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progressValue = Math.min(elapsed / duration, 1);
      const value = Math.round(start + (end - start) * progressValue);

      // Update progress in map
      progress.percentage = value;
      this.generationProgressMap.set(generationId, progress);

      if (progressValue < 1) {
        requestAnimationFrame(animate);
      } else {
        progress.percentage = 100;
        this.generationProgressMap.set(generationId, progress);
        setTimeout(() => {
          // Mark as complete and stop animating
          progress.isComplete = true;
          progress.isAnimating = false;
          progress.percentage = 100;
          this.generationProgressMap.set(generationId, progress);
          this.completeGenerationUpdate(generatedImage, generationId);
        }, 200);
      }
    };

    requestAnimationFrame(animate);
  }

  private completeGenerationUpdate(generatedImage: IGenerateImage, generationId: string): void {
    const index = this.allMediaCache.findIndex(img => img.id === generationId);
    setTimeout(() => {
      if (index !== -1) {
        this.allMediaCache[index] = {
          ...this.allMediaCache[index],
          url: generatedImage.url
        };
        this.updateAllMediaCache([...this.allMediaCache]);
      } else {
        this.updateAllMediaCache([generatedImage, ...this.allMediaCache]);
      }
    }, 200);
  }

  addOrUpdateFetchedMedia(mediaItem: IGenerateImage): void {
    const index = this.allMediaCache.findIndex(item => item.id === mediaItem.id);
    if (index !== -1) {
      this.allMediaCache[index] = { ...this.allMediaCache[index], ...mediaItem };
    } else {
      this.allMediaCache.unshift(mediaItem);
    }
    this.allMedia$.next([...this.allMediaCache]);
  }

  resetUnreadCounter(): void {
    this.unreadCompletedGenerations$.next(0);
  }

  clearMediaCache(): void {
    this.cancelRequestsSubject.complete();
    this.allMediaCache = [];
    this.allMedia$.next([]);
    this.isDataLoaded = false;
    this.pendingGenerationsMap.clear();
    this.pendingGenerations$.next([]);
    this.unreadCompletedGenerations$.next(0);
    this.isInitialLoadComplete = false;
  }
}
